import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";

/**
 * QuotaBlockBanner — shared amber/red banner used by every quota-aware
 * UI (PostEditorDialog, SchedulePostDialog, ContentManager, Calendar,
 * Generate). Renders nothing when `hidden`.
 *
 * The frontend is a *convenience* pre-check; the backend still enforces
 * the same rule via HTTP 402. Both messages match on wording.
 */
export default function QuotaBlockBanner({
  hidden,
  tone = "warn",
  title,
  message,
  primaryHref = "/billing",
  primaryLabel = "Upgrade",
  secondaryAction,   // { label, onClick }
  testid = "quota-block-banner",
}) {
  if (hidden) return null;
  const border =
    tone === "danger"
      ? "border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.06)]"
      : "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)]";
  const iconColor = tone === "danger" ? "text-[color:var(--danger)]" : "text-[color:var(--accent-warm)]";
  return (
    <div
      className={`rounded-md border p-3 text-xs flex items-start gap-2 flex-wrap ${border}`}
      role="alert"
      data-testid={testid}
    >
      <AlertCircle className={`h-4 w-4 mt-0.5 shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0 text-[color:var(--text-2)] leading-relaxed">
        {title ? (
          <div className="font-semibold text-[color:var(--text-1)] mb-0.5">{title}</div>
        ) : null}
        <div>{message}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {secondaryAction ? (
          <button
            type="button"
            onClick={secondaryAction.onClick}
            className="text-[color:var(--text-1)] underline text-xs"
            data-testid={`${testid}-secondary`}
          >
            {secondaryAction.label}
          </button>
        ) : null}
        <Link
          to={primaryHref}
          className="inline-flex items-center gap-1 rounded-md bg-[color:var(--accent-warm)] hover:opacity-90 text-[color:var(--bg-0)] font-semibold px-2.5 py-1.5 text-xs"
          data-testid={`${testid}-primary`}
        >
          {primaryLabel}
        </Link>
      </div>
    </div>
  );
}
